import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { getParticipantEntryCompetitionDistanceIds } from 'src/helpers/participantList';
import { getParticipantRegistrationStatus, WizardRegistrationStatus } from 'src/helpers/userCheckinWizard';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import { IBaseTechnicalType } from 'src/interfaces/TechnicalType';

interface Props {
  participants: IEnrichedParticipant[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  competitionsById?: Record<string, IBaseTechnicalType>;
  distancesById?: Record<string, IBaseTechnicalType>;
}

function statusChip(
  status: WizardRegistrationStatus,
  t: (key: string) => string,
) {
  const color =
    status === 'checkedIn' ? 'success' : status === 'registered' ? 'info' : 'default';
  const label =
    status === 'checkedIn'
      ? t('participants.checkedIn')
      : status === 'registered'
        ? t('registrations.registered')
        : t('participants.noRegistration');
  return <Chip size="small" color={color} label={label} />;
}

export default function SelectParticipantsStep({
  participants,
  selectedIds,
  onSelectedIdsChange,
  competitionsById,
  distancesById,
}: Props) {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const allSelected = participants.length > 0 && selectedIds.length === participants.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      onSelectedIdsChange([]);
    } else {
      onSelectedIdsChange(participants.map(p => p.id));
    }
  };

  const toggleOne = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectedIdsChange(selectedIds.filter(x => x !== id));
    } else {
      onSelectedIdsChange([...selectedIds, id]);
    }
  };

  if (!participants.length) {
    return (
      <Typography color="text.secondary">{t('userCheckinWizard.noParticipantsForUser')}</Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={toggleAll}
                slotProps={{ input: { 'aria-label': t('userCheckinWizard.selectAll') } }}
              />
            </TableCell>
            <TableCell>{t('form.name')}</TableCell>
            <TableCell>{t('userCheckinWizard.statusColumn')}</TableCell>
            <TableCell>{t('drawer.competitions')}</TableCell>
            <TableCell>{t('drawer.distances')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {participants.map(p => {
            const status = getParticipantRegistrationStatus(p);
            const { competitionId, distanceId } = getParticipantEntryCompetitionDistanceIds(p);
            const competition = competitionId ? competitionsById?.[competitionId] : undefined;
            const distance = distanceId ? distancesById?.[distanceId] : undefined;
            return (
              <TableRow key={p.id} hover selected={selectedIds.includes(p.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(p.id)}
                    onChange={() => toggleOne(p.id)}
                  />
                </TableCell>
                <TableCell>
                  {p.lastName} {p.firstName}
                </TableCell>
                <TableCell>{statusChip(status, t)}</TableCell>
                <TableCell>{competition?.i18n[currentLang] ?? '-'}</TableCell>
                <TableCell>{distance?.i18n[currentLang] ?? '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
